import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { type ProductionOrderSummaryType } from '@aeris/contracts'
import {
  Button,
  CheckboxField,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  InlineAlert,
} from '@aeris/ui'

import { fetchUniformPrinters, moveProductionStage } from './api/uniforms.ts'
import { ApiError } from './api.ts'
import { type UniformStage } from './production-stage-meta.tsx'

export type MovableOrder = Pick<
  ProductionOrderSummaryType,
  'id' | 'stage' | 'printers'
>

type Move = {
  readonly order: MovableOrder
  readonly to: UniformStage
  readonly printerIds: string[]
  readonly force: boolean
}

/**
 * Trocar a etapa de um pedido — o mesmo gesto na linha da fila, no cartão do
 * quadro e na ficha (F170, F171).
 *
 * Duas perguntas podem aparecer no meio:
 *
 * - **"Em qual impressora?"** ao ir para Imprimindo: é o momento em que a
 *   resposta existe, e é ela que o pedido repetido procura meses depois;
 * - **"Mandar sem a arte aprovada?"**, só para quem gerencia a produção, quando
 *   o servidor recusa com `art_not_approved`. Para os outros a recusa é a
 *   palavra final, e a mensagem diz quem destrava.
 *
 * A troca manda a etapa que a tela MOSTRAVA; se alguém mudou antes, o servidor
 * recusa (`stage_changed`) e a fila recarrega.
 *
 * Um só por tela serve a todos os pedidos dela: o quadro solta o cartão numa
 * coluna e chama `move(pedido, etapa)`, sem um diálogo por cartão.
 */
export const useStageMove = (options: {
  readonly manages: boolean
}): {
  readonly move: (order: MovableOrder, to: UniformStage) => void
  /** O pedido cuja troca está a caminho. */
  readonly pendingId: string | null
  /** A última recusa, e de qual pedido. */
  readonly error: { readonly orderId: string; readonly message: string } | null
  /** Os diálogos — impressora e confirmação. Renderize uma vez na tela. */
  readonly dialogs: ReactNode
} => {
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState<MovableOrder | null>(null)
  const [forcing, setForcing] = useState<Move | null>(null)
  const [chosen, setChosen] = useState<readonly string[]>([])
  const [error, setError] = useState<{
    orderId: string
    message: string
  } | null>(null)

  const printers = useQuery({
    queryKey: ['uniform-printers', 'active'],
    queryFn: async ({ signal }) => await fetchUniformPrinters('active', signal),
    enabled: asking !== null,
  })

  const refresh = async (orderId: string): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['production-queue'] })
    await queryClient.invalidateQueries({
      queryKey: ['production-order', orderId],
    })
  }

  const mutation = useMutation({
    mutationFn: async (input: Move) =>
      await moveProductionStage(input.order.id, {
        from: input.order.stage,
        to: input.to,
        printerIds: input.printerIds,
        force: input.force,
      }),
    onSuccess: async (_result, input) => {
      setAsking(null)
      setForcing(null)
      setError(null)
      await refresh(input.order.id)
    },
    onError: async (cause: unknown, input) => {
      if (
        cause instanceof ApiError &&
        cause.code === 'art_not_approved' &&
        options.manages &&
        !input.force
      ) {
        setAsking(null)
        setForcing(input)
        return
      }
      setError({
        orderId: input.order.id,
        message:
          cause instanceof ApiError
            ? cause.message
            : 'Não foi possível trocar a etapa.',
      })
      // Quem mudou antes ganha: a fila mostra o que está gravado.
      await refresh(input.order.id)
    },
  })

  /*
   * Só as ativas vão: a máquina arquivada que o pedido já tinha continua na
   * ficha, mas ninguém imprime nela de novo.
   */
  const activeChosen = chosen.filter((id) =>
    (printers.data ?? []).some((printer) => printer.id === id)
  )

  const move = (order: MovableOrder, to: UniformStage): void => {
    if (to === order.stage) return
    setError(null)
    if (to === 'imprimindo') {
      setChosen(order.printers.map((printer) => printer.id))
      setAsking(order)
      return
    }
    mutation.mutate({ order, to, printerIds: [], force: false })
  }

  const dialogs = (
    <>
      {asking === null ? null : (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setAsking(null)
          }}
        >
          <DialogContent
            title="Em qual impressora?"
            description="É por aqui que se sabe onde repetir este pedido. Marque todas as que ele usar."
          >
            <div className="flex flex-col gap-2">
              {(printers.data ?? []).length === 0 && !printers.isPending ? (
                <InlineAlert tone="warning">
                  Nenhuma impressora cadastrada. Cadastre em Configurações →
                  Impressoras.
                </InlineAlert>
              ) : null}
              {(printers.data ?? []).map((printer) => (
                <CheckboxField
                  key={printer.id}
                  label={printer.name}
                  checked={chosen.includes(printer.id)}
                  onCheckedChange={(checked) => {
                    setChosen((current) =>
                      checked === true
                        ? [...current, printer.id]
                        : current.filter((id) => id !== printer.id)
                    )
                  }}
                />
              ))}
              {error?.orderId === asking.id ? (
                <p
                  role="alert"
                  className="text-(length:--text-sm) text-(--danger)"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => {
                  setAsking(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={activeChosen.length === 0 || mutation.isPending}
                onClick={() => {
                  mutation.mutate({
                    order: asking,
                    to: 'imprimindo',
                    printerIds: activeChosen,
                    force: false,
                  })
                }}
              >
                Imprimindo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={forcing !== null}
        onOpenChange={(open) => {
          if (!open) setForcing(null)
        }}
        title="A arte ainda não foi aprovada"
        description="O cliente não aprovou a arte deste pedido. Mandar para a fábrica mesmo assim? Fica escrito na linha do tempo que foi sem aprovação."
        confirmLabel="Mandar mesmo assim"
        tone="danger"
        loading={mutation.isPending}
        onConfirm={() => {
          if (forcing !== null) mutation.mutate({ ...forcing, force: true })
        }}
      />
    </>
  )

  return {
    move,
    pendingId: mutation.isPending ? mutation.variables.order.id : null,
    error,
    dialogs,
  }
}
